import { useCurrentDay, useWindow } from '@krp/client-runtime'
import {
  PlaceScreen,
  SCHEDULE_WINDOWS,
  cashierRemembered,
  combinedView,
  isMonthValue,
  monthCalendar,
  monthOfDay,
  placeGridOf,
  scheduleAccess,
  scheduleAssignmentsAll,
  scheduleMemberAdd,
  scheduleMemberRemove,
  scheduleMembers,
  schedulePlaces,
  shiftMonth,
  shownError,
  type PlaceActions,
  type PlaceVm,
} from '@krp/schedule-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useCashierDirectory, useCellCommands, viewOf } from './scheduleKit'

/**
 * Экран объекта: объект и месяц — из адреса окна (`/schedule/places/:placeId/:month`, без месяца — текущий). Таблица строится
 * из трёх запросов: состав объекта (хранит сервер), назначения ВСЕХ объектов за месяц (из них же пометки «занято на другом
 * объекте») и объекты. Клик по клетке — одна команда (`useCellCommands`): клетка «сохраняется…» до ответа, отказ показан целиком.
 * Состав правится командами «добавить» (окно «Добавить сотрудника» шлёт их по одной, отказ показывает у себя) и «убрать»; «убрать» (после подтверждения на экране) снимает и выходы кассира с сегодняшнего дня; отказ любой из них показывается тем же блоком.
 */
export function SchedulePlaceBinding() {
  const dispatch = useAppDispatch()
  const { params, replaceWith } = useWindow()
  const today = useCurrentDay()
  const directory = useCashierDirectory()
  const commands = useCellCommands()
  const recent = useAppSelector((state) => state.schedule.recent.ids)

  const placeId = params.placeId ?? ''
  const requested = params.month ?? ''
  const month = isMonthValue(requested) ? requested : monthOfDay(today)
  const calendar = monthCalendar(month, today)

  const access = scheduleAccess.useQuery()
  const places = schedulePlaces.useQuery({ includeArchived: true })
  const members = scheduleMembers.useQuery({ placeId })
  const assignments = scheduleAssignmentsAll.useQuery({ from: calendar.from, to: calendar.to })
  const [addMember] = scheduleMemberAdd.useMutation()
  const [removeMember] = scheduleMemberRemove.useMutation()

  const place = places.data?.find((item) => item.id === placeId) ?? null
  const placeNameOf = (id: string): string => places.data?.find((item) => item.id === id)?.name ?? 'другой объект'
  const archivedPlaceIds = new Set((places.data ?? []).filter((item) => item.archived).map((item) => item.id.toLowerCase()))
  const memberIds = (members.currentData ?? []).map((member) => member.cashierId)
  const goToMonth = (next: string) => replaceWith(SCHEDULE_WINDOWS.place, { placeId, month: next })

  const load = combinedView([viewOf(access), viewOf(places), viewOf({ ...members, data: members.currentData }), viewOf({ ...assignments, data: assignments.currentData })])
  const vm: PlaceVm = {
    load,
    refreshing: load.refreshing,
    editable: access.data?.canManage === true && place !== null && !place.archived,
    placeName: place?.name ?? null,
    archived: place?.archived === true,
    title: calendar.title,
    days: calendar.days,
    rows: placeGridOf({
      days: calendar.days,
      placeId,
      memberIds,
      items: assignments.currentData ?? [],
      saving: commands.pending,
      placeNameOf,
      archivedPlaceIds,
      nameOf: directory.nameOf,
    }),
    error: commands.error,
    denied: commands.denied,
    people: directory.people,
    memberIds,
    recent,
    directoryError: directory.status === 'unavailable' ? directory.error : null,
  }
  const actions: PlaceActions = {
    previous: () => goToMonth(shiftMonth(month, -1)),
    next: () => goToMonth(shiftMonth(month, 1)),
    today: () => goToMonth(monthOfDay(today)),
    toggle: commands.toggle,
    setKind: commands.setKind,
    dismissDenied: commands.dismissDenied,
    addMember: async (cashierId) => {
      commands.clear()
      try {
        await addMember({ placeId, cashierId }).unwrap()
      } catch (failure: unknown) {
        return shownError('Не удалось добавить кассира в состав объекта', failure)
      }
      dispatch(cashierRemembered(cashierId))
      return null
    },
    removeMember: (cashierId) => {
      commands.clear()
      void removeMember({ placeId, cashierId, clearFrom: today })
        .unwrap()
        .catch((failure: unknown) => commands.fail(shownError('Не удалось убрать кассира из состава объекта', failure)))
    },
    reload: () => {
      void access.refetch()
      void places.refetch()
      void members.refetch()
      void assignments.refetch()
    },
  }
  return <PlaceScreen vm={vm} actions={actions} />
}
